import { useProjectStore } from '@/core/project/store/project.store';
import { SceneNode } from '@/features/screenplay/components/editor/nodes/SceneNode';
import { create } from 'zustand';
import { shotListRepository } from '../repository/ShotListRepository';

import type { CameraAngle, CameraMovement, Framing, Shot, ShotPriority, ShotType } from '@storyverse/shared';

export type { CameraAngle, CameraMovement, Framing, Shot, ShotPriority, ShotType };
export interface ShotListState {
    shotLists: Record<string, Shot[]> // Key is sceneId
    isGenerating: boolean
    showScenePreview: boolean
    setShowScenePreview: (isScenePreviewPanelOpen: boolean) => void
    loadShotList: (sceneId: string) => Promise<void>
    selectedSceneNode: SceneNode | null
    setSelectedSceneNode: (sceneNode: SceneNode) => void
    setShotList: (sceneId: string, shots: Shot[]) => void
    updateShot: (sceneId: string, shotId: string, updatedShot: Partial<Shot>) => void
    addShot: (sceneId: string, shot: Shot) => void
    moveShot: (sceneId: string, fromIndex: number, toIndex: number) => void
    removeShot: (sceneId: string, shotId: string) => void
    setIsGenerating: (isGenerating: boolean) => void
}

export const useShotListStore = create<ShotListState>((set, get) => ({
    shotLists: {},
    selectedSceneNode: null,
    isGenerating: false,
    showScenePreview: false,
    setShowScenePreview: (isScenePreviewPanelOpen: boolean) => set({ showScenePreview: isScenePreviewPanelOpen }),
    setSelectedSceneNode: (sceneNode: SceneNode) => set({ selectedSceneNode: sceneNode }),
    loadShotList: async (sceneId) => {
        const { project } = useProjectStore.getState()
        const projectId = project?.id
        console.log("Loading shot list for scene:", sceneId, "Project:", projectId)
        if (!projectId) {
            console.warn("Cannot load shot list: No projectId set in store", { sceneId })
            return
        }

        console.log("Loading shot list from Repository for scene:", sceneId)
        try {
            const entity = await shotListRepository.getBySceneId(projectId, sceneId)

            if (entity) {
                const shots = entity?.shots || []
                set((state) => ({
                    shotLists: { ...state.shotLists, [sceneId]: shots }
                }))
            }
        } catch (error) {
            console.error("Failed to load shot list:", error)
        }
    },

    setShotList: (sceneId, shots) => {
        set((state) => ({
            shotLists: { ...state.shotLists, [sceneId]: shots }
        }))
        // Persist via Repository
        const { project } = useProjectStore.getState()
        const projectId = project?.id
        if (projectId) {
            shotListRepository.saveShots(projectId, sceneId, shots).catch(err =>
                console.error("Error saving shot list:", err)
            )
        } else {
            console.error("Failed to save shot list: No projectId set")
        }
    },

    updateShot: (sceneId, shotId, updatedShot) => {
        const { shotLists } = get()

        const { project } = useProjectStore.getState()
        const projectId = project?.id
        const currentList = shotLists[sceneId] || []
        const updatedList = currentList.map(shot =>
            shot.id === shotId ? { ...shot, ...updatedShot } : shot
        )

        set((state) => ({ shotLists: { ...state.shotLists, [sceneId]: updatedList } }))

        if (projectId) {
            shotListRepository.saveShots(projectId, sceneId, updatedList).catch(err =>
                console.error("Error saving updated shot list:", err)
            )
        } else {
            console.error("Failed to update shot: No projectId set")
        }
    },

    addShot: (sceneId, shot) => {
        const { shotLists } = get()

        const { project } = useProjectStore.getState()
        const projectId = project?.id
        const updatedList = [...(shotLists[sceneId] || []), shot]

        set((state) => ({
            shotLists: { ...state.shotLists, [sceneId]: updatedList }
        }))

        if (projectId) {
            console.log("Saving new shot to DB...", { projectId, sceneId })
            shotListRepository.saveShots(projectId, sceneId, updatedList).catch(err =>
                console.error("Error saving new shot:", err)
            )
        } else {
            console.error("Failed to add shot: No projectId set")
        }
    },

    moveShot: (sceneId, fromIndex, toIndex) => {
        const { shotLists } = get()
        const { project } = useProjectStore.getState()
        const projectId = project?.id
        const currentList = [...(shotLists[sceneId] || [])]

        if (
            fromIndex < 0 ||
            toIndex < 0 ||
            fromIndex >= currentList.length ||
            toIndex >= currentList.length ||
            fromIndex === toIndex
        ) {
            return
        }

        const [movedShot] = currentList.splice(fromIndex, 1)
        currentList.splice(toIndex, 0, movedShot)

        set((state) => ({
            shotLists: { ...state.shotLists, [sceneId]: currentList }
        }))

        if (projectId) {
            shotListRepository.saveShots(projectId, sceneId, currentList).catch(err =>
                console.error("Error reordering shots:", err)
            )
        } else {
            console.error("Failed to reorder shots: No projectId set")
        }
    },

    removeShot: (sceneId, shotId) => {
        const { shotLists } = get()
        const { project } = useProjectStore.getState()
        const projectId = project?.id
        const updatedList = (shotLists[sceneId] || []).filter(s => s.id !== shotId)

        set((state) => ({
            shotLists: { ...state.shotLists, [sceneId]: updatedList }
        }))

        if (projectId) {
            shotListRepository.saveShots(projectId, sceneId, updatedList).catch(err =>
                console.error("Error removing shot:", err)
            )
        } else {
            console.error("Failed to remove shot: No projectId set")
        }
    },

    setIsGenerating: (isGenerating) => set({ isGenerating }),
}))
